var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, css, html, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { ifDefined } from "lit/directives/if-defined.js";
import { componentUtilities } from "~/ui/styles/utilities";
import { spriteHref, spritePaths } from "~/utils/sprite";
export const MARKETING_BUTTON_TAG = "mkt-button";
let MarketingButtonElement = class MarketingButtonElement extends LitElement {
    constructor() {
        super(...arguments);
        this.type = "button";
        this.variant = "solid";
        this.color = "primary";
        this.size = "md";
        this.disabled = false;
        this.hasPrefix = false;
        this.hasSuffix = false;
        this.onPrefixSlotChange = (event) => {
            const slot = event.target;
            this.hasPrefix = slot.assignedElements({ flatten: true }).length > 0;
        };
        this.onSuffixSlotChange = (event) => {
            const slot = event.target;
            this.hasSuffix = slot.assignedElements({ flatten: true }).length > 0;
        };
        this.onClick = (event) => {
            if (this.disabled) {
                event.preventDefault();
                event.stopImmediatePropagation();
            }
        };
    }
    firstUpdated() {
        const prefixSlot = this.renderRoot.querySelector('slot[name="prefix"]');
        const suffixSlot = this.renderRoot.querySelector('slot[name="suffix"]');
        if (prefixSlot instanceof HTMLSlotElement) {
            this.hasPrefix = prefixSlot.assignedElements({ flatten: true }).length > 0;
        }
        if (suffixSlot instanceof HTMLSlotElement) {
            this.hasSuffix = suffixSlot.assignedElements({ flatten: true }).length > 0;
        }
    }
    getComputedRel() {
        if (this.rel) {
            return this.rel;
        }
        return this.target === "_blank" ? "noopener noreferrer" : undefined;
    }
    render() {
        const prefixIconHref = this.prefixIcon
            ? spriteHref(spritePaths.marketing, this.prefixIcon)
            : undefined;
        const suffixIconHref = this.suffixIcon
            ? spriteHref(spritePaths.marketing, this.suffixIcon)
            : undefined;
        const renderIcon = (iconHref, part) => iconHref
            ? html `
                      <span part=${part} class="icon" aria-hidden="true">
                          <svg focusable="false">
                              <use href=${iconHref}></use>
                          </svg>
                      </span>
                  `
            : nothing;
        const prefix = html `
            <span part="prefix" class="slot" ?hidden=${!prefixIconHref && !this.hasPrefix}>
                ${renderIcon(prefixIconHref, "prefix-icon")}
                <slot name="prefix" @slotchange=${this.onPrefixSlotChange}></slot>
            </span>
        `;
        const suffix = html `
            <span part="suffix" class="slot" ?hidden=${!suffixIconHref && !this.hasSuffix}>
                <slot name="suffix" @slotchange=${this.onSuffixSlotChange}></slot>
                ${renderIcon(suffixIconHref, "suffix-icon")}
            </span>
        `;
        const content = html `
            <span class="inner">
                ${prefix}
                <span part="label" class="label">
                    <slot></slot>
                </span>
                ${suffix}
            </span>
        `;
        if (this.href) {
            return html `
                <a
                    part="base"
                    class="btn"
                    href=${ifDefined(this.disabled ? undefined : this.href)}
                    target=${ifDefined(this.disabled ? undefined : this.target)}
                    rel=${ifDefined(this.disabled ? undefined : this.getComputedRel())}
                    aria-disabled=${ifDefined(this.disabled ? "true" : undefined)}
                    tabindex=${ifDefined(this.disabled ? "-1" : undefined)}
                    @click=${this.onClick}
                >
                    ${content}
                </a>
            `;
        }
        return html `
            <button
                part="base"
                class="btn"
                type=${this.type}
                ?disabled=${this.disabled}
                @click=${this.onClick}
            >
                ${content}
            </button>
        `;
    }
    static { this.styles = [
        componentUtilities,
        css `
            * {
                box-sizing: border-box;
            }

            [hidden] {
                display: none !important;
            }

            :host {
                --mkt-btn-bg: var(--mkt-color-red, #de3341);
                --mkt-btn-bg-hover: var(--mkt-color-red, #de3341);
                --mkt-btn-text: var(--color-white, #fff);
                --mkt-btn-radius: var(--radius-pill);
                --mkt-btn-ring: color-mix(in oklab, var(--mkt-btn-bg) 30%, white 70%);
                --mkt-btn-font: 500 14px/1.435rem var(--font-display, inherit);
                --mkt-btn-padding-x: 20px;
                --mkt-btn-gap: calc(var(--spacing) * 1.5);

                display: inline-flex;
                width: fit-content;
            }

            :host([color="primary"]) {
                --mkt-btn-bg: var(--mkt-color-red);
                --mkt-btn-bgHover: color-mix(in srgb, black 15%, var(--mkt-color-red));
            }
            :host([color="secondary"]) {
                --mkt-btn-bg: var(--mkt-color-red);
                --mkt-btn-bgHover: yellow;
            }

            :host([size="sm"]) {
                --mkt-btn-height: 32px;
            }
            :host([size="md"]) {
                --mkt-btn-height: 40px;

                @media (width > 768px) {
                    --mkt-btn-padding-x: 28px;
                }
            }

            .btn {
                display: inline-flex;
                align-items: center;
                justify-content: center;
                height: var(--mkt-btn-height, fit-content);
                padding-inline: var(--mkt-btn-padding-x);
                background-color: var(--mkt-btn-bg);
                color: var(--mkt-btn-text);
                border: 1px solid transparent;
                border-radius: var(--mkt-btn-radius);
                position: relative;
                white-space: nowrap;
                text-decoration: none;
                font: var(--mkt-btn-font);
                text-align: center;
                width: 100%;
                transition:
                    background-color var(--transition-duration-faster) var(--ease-in-out-quad),
                    color var(--transition-duration-faster) var(--ease-in-out-quad);
                cursor: pointer;
                user-select: none;


                &:hover {
                    background: var(--mkt-btn-bgHover, var(--mkt-btn-bg));
                }

                &:active {
                    transform: translateY(1px) scale(0.985);
                }
            }

            .inner {
                display: flex;
                align-items: center;
                justify-content: center;
                gap: var(--mkt-btn-gap);
            }

            .slot,
            .label {
                display: inline-flex;
                align-items: center;
            }


            ::slotted([slot="prefix"]),
            ::slotted([slot="suffix"]) {
                inline-size: 1em;
                block-size: 1em;
                flex: none;
            }
        `,
    ]; }
};
__decorate([
    property()
], MarketingButtonElement.prototype, "href", void 0);
__decorate([
    property()
], MarketingButtonElement.prototype, "target", void 0);
__decorate([
    property()
], MarketingButtonElement.prototype, "rel", void 0);
__decorate([
    property()
], MarketingButtonElement.prototype, "type", void 0);
__decorate([
    property({ reflect: true })
], MarketingButtonElement.prototype, "variant", void 0);
__decorate([
    property({ reflect: true })
], MarketingButtonElement.prototype, "color", void 0);
__decorate([
    property({ reflect: true })
], MarketingButtonElement.prototype, "size", void 0);
__decorate([
    property({ attribute: "prefix-icon" })
], MarketingButtonElement.prototype, "prefixIcon", void 0);
__decorate([
    property({ attribute: "suffix-icon" })
], MarketingButtonElement.prototype, "suffixIcon", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], MarketingButtonElement.prototype, "disabled", void 0);
__decorate([
    state()
], MarketingButtonElement.prototype, "hasPrefix", void 0);
__decorate([
    state()
], MarketingButtonElement.prototype, "hasSuffix", void 0);
MarketingButtonElement = __decorate([
    customElement(MARKETING_BUTTON_TAG)
], MarketingButtonElement);
export { MarketingButtonElement };
